import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Accessibility, X, Contrast, Type } from 'lucide-react';

const AccessibilityMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const [highContrast, setHighContrast] = useState(false);
  const [grayscale, setGrayscale] = useState(false);
  const [linkHighlight, setLinkHighlight] = useState(false);

  useEffect(() => {
    const savedSettings = localStorage.getItem('accessibilitySettings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      setFontSize(settings.fontSize);
      setHighContrast(settings.highContrast);
      setGrayscale(settings.grayscale);
      setLinkHighlight(settings.linkHighlight);
      applySettings(settings);
    }
  }, []);

  const applySettings = (settings) => {
    document.documentElement.style.fontSize = `${settings.fontSize}%`;
    const wrapper = document.getElementById('accessibility-wrapper');
    if (!wrapper) return;

    wrapper.classList.toggle('high-contrast', settings.highContrast);
    wrapper.classList.toggle('grayscale', settings.grayscale);
    wrapper.classList.toggle('highlight-links', settings.linkHighlight);
  };

  const saveSettings = (settings) => {
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
  };

  const handleSettingChange = (key, value) => {
    const newSettings = {
      fontSize,
      highContrast,
      grayscale,
      linkHighlight,
      [key]: value,
    };

    setFontSize(newSettings.fontSize);
    setHighContrast(newSettings.highContrast);
    setGrayscale(newSettings.grayscale);
    setLinkHighlight(newSettings.linkHighlight);
    applySettings(newSettings);
    saveSettings(newSettings);
  };

  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 100,
      highContrast: false,
      grayscale: false,
      linkHighlight: false,
    };
    setFontSize(100);
    setHighContrast(false);
    setGrayscale(false);
    setLinkHighlight(false);
    applySettings(defaultSettings);
    saveSettings(defaultSettings);
  };

  const menu = (
    <div
      className="fixed bottom-16 left-4 z-[10000] w-[320px] rounded-xl border bg-white p-4 shadow-lg text-sm"
      style={{ filter: 'none' }}
    >
      <button
        onClick={() => setIsOpen(false)}
        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        aria-label="סגור"
      >
        <X size={18} />
      </button>

      <h2 className="text-center text-lg font-bold mb-4">🔧 הגדרות נגישות</h2>

      <div className="space-y-4">
        {/* Font size */}
        <div className="flex justify-between items-center">
          <label className="flex items-center gap-2 font-medium text-gray-800">
            <Type size={16} /> גודל טקסט
          </label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSettingChange('fontSize', Math.max(80, fontSize - 10))}
              className="border px-2 rounded text-xs"
            >
              A-
            </button>
            <span className="w-10 text-center">{fontSize}%</span>
            <button
              onClick={() => handleSettingChange('fontSize', Math.min(150, fontSize + 10))}
              className="border px-2 rounded text-xs"
            >
              A+
            </button>
          </div>
        </div>

        {/* Contrast */}
        <div className="flex justify-between items-center">
          <label className="flex items-center gap-2 font-medium text-gray-800">
            <Contrast size={16} /> ניגודיות
          </label>
          <input
            type="checkbox"
            checked={highContrast}
            onChange={(e) => handleSettingChange('highContrast', e.target.checked)}
          />
        </div>

        {/* Grayscale */}
        <div className="flex justify-between items-center">
          <label className="flex items-center gap-2 font-medium text-gray-800">
            🎨 גווני אפור
          </label>
          <input
            type="checkbox"
            checked={grayscale}
            onChange={(e) => handleSettingChange('grayscale', e.target.checked)}
          />
        </div>

        {/* Highlight links */}
        <div className="flex justify-between items-center">
          <label className="flex items-center gap-2 font-medium text-gray-800">
            🔗 הדגשת קישורים
          </label>
          <input
            type="checkbox"
            checked={linkHighlight}
            onChange={(e) => handleSettingChange('linkHighlight', e.target.checked)}
          />
        </div>

        <button
          onClick={resetSettings}
          className="w-full mt-2 rounded bg-gray-100 py-1 hover:bg-gray-200"
        >
          🔄 איפוס הגדרות
        </button>
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-[10000] bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 focus:outline-none"
        aria-label="נגישות"
      >
        <Accessibility size={18} />
      </button>

      {isOpen && ReactDOM.createPortal(menu, document.body)}

       <style>{`
        .high-contrast {
          filter: contrast(200%) !important;
        }

        .grayscale {
          filter: grayscale(100%) !important;
        }

        .highlight-links a {
          background-color: yellow !important;
          color: black !important;
          font-weight: bold !important;
          text-decoration: underline !important;
        }
      `}</style>
    </>
  );
};

export default AccessibilityMenu;